import React, { useState } from "react";
import { Layers, Loader2, Sparkles, Copy, Check, Clock, Instagram, Facebook, Linkedin, Film, Hash } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { multiPlatformGenerate } from "../../api";
import { useToast } from "../../hooks/use-toast";

const area = "w-full px-4 py-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none";
const TONES = ["Warm & friendly", "Professional", "Luxury", "Playful", "Urgent", "Informative"];

const PlatformCard = ({ icon: Icon, colour, title, badge, children }) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const text = typeof children === "string" ? children : "";
  const copy = async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch { toast({ title: "Couldn't copy" }); } };
  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-5" data-testid={`mp-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-2 text-[13px] font-500 text-[#2b2b2b]"><span className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: colour }}><Icon size={16} /></span> {title}{badge && <span className="text-[10px] uppercase tracking-wide text-[#b8935f]">· {badge}</span>}</span>
        {text && <button onClick={copy} className="inline-flex items-center gap-1 text-[12px] text-[#8a8a8a] hover:text-[#b8935f] transition-colors" data-testid="mp-copy">{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}</button>}
      </div>
      <div className="text-[13px] text-[#3a3a3a] whitespace-pre-wrap leading-relaxed">{children}</div>
    </div>
  );
};

const MultiPlatform = ({ properties = [], onSaved }) => {
  const { toast } = useToast();
  const [brief, setBrief] = useState("");
  const [propertyId, setPropertyId] = useState("none");
  const [tone, setTone] = useState("");
  const [busy, setBusy] = useState(false);
  const [pack, setPack] = useState(null);

  const run = async () => {
    if (!brief.trim() && propertyId === "none") { toast({ title: "Add a brief or pick a property first" }); return; }
    setBusy(true); setPack(null);
    try {
      const r = await multiPlatformGenerate({ brief: brief.trim(), propertyId: propertyId === "none" ? null : propertyId, tone });
      setPack(r);
      toast({ title: `${r.count} platform drafts created`, description: "Saved to Pending Review." });
      onSaved && onSaved();
    } catch (e) {
      toast({ title: "Generation failed", description: e?.response?.data?.detail || "Please try again." });
    } finally { setBusy(false); }
  };

  const p = pack?.pack || {};
  const reel = p.reel || {};
  const tiktok = p.tiktok || {};

  return (
    <div className="grid lg:grid-cols-[400px_1fr] gap-8" data-testid="multi-platform">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6 h-fit">
        <h3 className="font-display text-[22px] font-400 text-[#2b2b2b] mb-1 flex items-center gap-2"><Layers size={20} className="text-[#b8935f]" /> Multi-Platform Generator</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-5">One brief → a full pack for every platform: Reel script + shot list, IG caption, Facebook, LinkedIn, TikTok, hooks, hashtags & best time.</p>

        <label className="text-[12px] text-[#6a6a6a] mb-1 block">Feature a listing (optional)</label>
        <Select value={propertyId} onValueChange={setPropertyId}>
          <SelectTrigger className="h-[46px] border-[#e2d8c8] mb-4" data-testid="mp-property"><SelectValue placeholder="None" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No specific listing</SelectItem>
            {properties.map((pr) => <SelectItem key={pr.id} value={pr.id}>{pr.title}</SelectItem>)}
          </SelectContent>
        </Select>

        <label className="text-[12px] text-[#6a6a6a] mb-1 block">What's it about?</label>
        <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3} placeholder="e.g. Just listed: characterful 3-bed in Widcombe, open house Saturday" className={area + " mb-4"} data-testid="mp-brief" />

        <label className="text-[12px] text-[#6a6a6a] mb-1 block">Tone</label>
        <Select value={tone} onValueChange={setTone}>
          <SelectTrigger className="h-[46px] border-[#e2d8c8] mb-5" data-testid="mp-tone"><SelectValue placeholder="Brand default" /></SelectTrigger>
          <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>

        <button onClick={run} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 h-[50px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60" data-testid="mp-generate">
          {busy ? <><Loader2 size={17} className="animate-spin" /> Building your content pack…</> : <><Sparkles size={17} /> Generate all platforms</>}
        </button>
      </div>

      <div>
        {!pack && !busy && (
          <div className="h-full min-h-[360px] rounded-2xl border border-dashed border-[#e2d8c8] bg-white/60 flex flex-col items-center justify-center text-center p-10">
            <Layers size={30} className="text-[#d9c4a6] mb-4" />
            <h4 className="font-display text-[20px] text-[#2b2b2b] mb-1">Your cross-platform pack appears here</h4>
            <p className="text-[13px] text-[#8a8a8a] max-w-[380px]">One brief becomes a Reel, IG caption, Facebook post, LinkedIn post and TikTok script — all on-brand, all at once.</p>
          </div>
        )}
        {busy && (
          <div className="h-full min-h-[360px] rounded-2xl border border-[#f0ece5] bg-white flex flex-col items-center justify-center text-center p-10">
            <Loader2 size={30} className="text-[#b8935f] animate-spin mb-4" />
            <p className="text-[14px] text-[#6a6a6a]">Writing platform-native copy for every channel…</p>
          </div>
        )}
        {pack && (
          <div className="space-y-4" data-testid="mp-result">
            <div className="flex flex-wrap items-center gap-3 bg-[#1c1c1e] rounded-2xl p-4">
              {pack.bestTime && <span className="inline-flex items-center gap-1.5 text-[13px] text-white"><Clock size={15} className="text-[#d9b98a]" /> Best time: <strong>{pack.bestTime}</strong></span>}
              {pack.bestTimeReason && <span className="text-[12px] text-white/50">{pack.bestTimeReason}</span>}
              <span className="ml-auto text-[11px] px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">{pack.count} drafts saved to Pending Review</span>
            </div>

            {(pack.hooks || []).length > 0 && (
              <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
                <p className="text-[12px] uppercase tracking-wide text-[#b8935f] font-500 mb-2">Alternative hooks</p>
                <ul className="space-y-1.5">{pack.hooks.map((h, i) => <li key={i} className="text-[13px] text-[#3a3a3a]">• {h}</li>)}</ul>
              </div>
            )}

            <PlatformCard icon={Film} colour="linear-gradient(135deg,#E1306C,#F77737)" title="Instagram Reel" badge="script + shots">
              {reel.hook && <p className="font-500 text-[#2b2b2b] mb-2">🎬 {reel.hook}</p>}
              {(reel.script || []).length > 0 && <><p className="text-[11px] uppercase text-[#a0a0a0] mb-1">On-screen beats</p>{reel.script.map((b, i) => <p key={i}>• {b}</p>)}</>}
              {(reel.shotList || []).length > 0 && <><p className="text-[11px] uppercase text-[#a0a0a0] mt-3 mb-1">Shot list</p>{reel.shotList.map((s, i) => <p key={i}>{i + 1}. {s}</p>)}</>}
              {reel.audioVibe && <p className="mt-3 text-[#6a6a6a]">🎵 {reel.audioVibe}</p>}
              {reel.cta && <p className="mt-1 text-[#b8935f]">→ {reel.cta}</p>}
            </PlatformCard>

            <PlatformCard icon={Instagram} colour="linear-gradient(135deg,#E1306C,#F77737)" title="Instagram Caption">{p.igCaption || "—"}</PlatformCard>
            <PlatformCard icon={Facebook} colour="#1877F2" title="Facebook Post">{p.facebookPost || "—"}</PlatformCard>
            <PlatformCard icon={Linkedin} colour="#0A66C2" title="LinkedIn Post">{p.linkedinPost || "—"}</PlatformCard>

            <PlatformCard icon={Film} colour="#111" title="TikTok Script">
              {tiktok.hook && <p className="font-500 text-[#2b2b2b] mb-2">🎬 {tiktok.hook}</p>}
              {(tiktok.script || []).map((b, i) => <p key={i}>• {b}</p>)}
              {tiktok.cta && <p className="mt-1 text-[#b8935f]">→ {tiktok.cta}</p>}
            </PlatformCard>

            {(pack.hashtags || []).length > 0 && (
              <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
                <p className="text-[12px] uppercase tracking-wide text-[#b8935f] font-500 mb-2 flex items-center gap-1.5"><Hash size={13} /> Hashtags</p>
                <div className="flex flex-wrap gap-1.5">{pack.hashtags.map((h) => <span key={h} className="text-[12px] px-2 py-0.5 rounded-full bg-[#faf7f2] border border-[#f0ece5] text-[#6a6a6a]">{h}</span>)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiPlatform;
